import { useTranslation } from 'react-i18next';
import { Link } from '@remix-run/react';
import { useCallback, useMemo, JSX } from 'react';
import cls from 'classnames';
import { Article, LegacyArticleUtil } from 'shared/models/legacy';
import { Image } from '~/components/image';
import Heart from '@lemon8/design/lib/icons/icon_assets/heart.svg';
import PlayIcon from '@lemon8/design/lib/icons/icon_assets/play_fill.svg';
import { DivisibleStack } from '~/components/divisible-stack';
import { useNumFormatter } from '~/utils/format/num-formatter';
import { useAtomValue, useSetAtom } from 'jotai';
import { ClickPosition } from '~/utils/click-position';
import { usePageActive } from '~/utils/active/use-page-active';
import useReflowClick from '~/utils/use-reflow-click';
import { $IsMobileUser, $IsPCUser } from '~/utils/is-mobile-user';
import { useViewTracking } from '~/utils/use-view-tracking';
import { useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { $CurrentPageModule } from '~/models/current-page-module';
import { $RelatedPostCarouselShow } from '@article/shared/atoms/related-post-carousel-show';
import { $CurrentPositionPrefix } from '~/models/current-position-prefix';
import { unescapeArticleContentChars } from '@lemon8/web-app-shared/util';
import { useSEOHref } from '~/utils/build-seo-url';
import { $Locale, $UserAgent } from '@lemon8/web-app-shared/atom';
import { EArticleClass } from 'shared/models';

import './style.scss';
import { $RelatedPostAIGCDesc } from '~/utils/libra/related-post-aigc-desc';

interface ArticleCardProps {
  article: Article;
  isLCP?: boolean;
  hitGpt?: boolean;

  /**
   * starts from 0
   */
  rank: number;
}

const RELATED_ARTICLE_CAROUSEL_HIDE_RANK = 9;

export const ArticleCard = (props: ArticleCardProps): JSX.Element => {
  const { region } = useAtomValue($Locale);
  const { isBot } = useAtomValue($UserAgent);

  const { article } = props;
  const {
    article: {
      imageList,
      articleClass,
      title,
      groupId,
      shortContent,
      largeImage,
      author,
      articleLikes,
      imprId,
      gptInfo = {},
    },
    isLCP = false,
    rank,
    hitGpt = false,
  } = props;
  const { title: gptTitle = '', supplements = '' } = gptInfo;
  const { t } = useTranslation();
  const formatNum = useNumFormatter();
  const active = usePageActive();
  const isMobileUser = useAtomValue($IsMobileUser);
  const isPCUser = useAtomValue($IsPCUser);
  const currentPageModule = useScopedAtomValue($CurrentPageModule);
  const prefix = useScopedAtomValue($CurrentPositionPrefix);
  const setRelatedPostCarouselShow = useSetAtom($RelatedPostCarouselShow);

  const posterImageList = useMemo(() => {
    const temp = imageList.length ? [...imageList] : [largeImage];
    return temp.slice(0, temp.length < 3 ? 1 : 3);
  }, [largeImage, imageList]);

  const finalTitle = useMemo(() => {
    if (!isPCUser && hitGpt) {
      return gptTitle || title;
    }
    return title;
  }, [hitGpt, gptTitle, title]);

  const articleHref = useSEOHref(LegacyArticleUtil.getArticleHref(article));

  const handleClickArticle = useReflowClick(
    {
      clickEvent: (_, bundle) => ({
        ...bundle,
        current_page_module: currentPageModule,
        current_position: prefix + ClickPosition.related_post,
        page_click_id: groupId,
        rank,
      }),
      reflowBehavior: () => {
        active({
          schemaOption: {
            pageType: articleClass === EArticleClass.VIDEO ? 'video' : 'article',
            groupId,
            authorId: author.userId,
          },
          ampExtra: {
            enter_position: prefix + ClickPosition.related_post,
          },
        });
      },
      defaultBehavior: (event) => {
        if (article.articleRegion && article.articleRegion !== region) {
          event?.preventDefault();
          window.open(articleHref, '_blank');
        }
      },
    },
    [active, groupId, articleClass, author, article, articleHref],
  );

  const authorHref = useSEOHref(LegacyArticleUtil.getAuthorPath(article));

  const handleClickAuthor = useReflowClick(
    {
      clickEvent: (_, bundle) => ({
        ...bundle,
        current_page_module: currentPageModule,
        current_position: prefix + ClickPosition.related_post,
        page_click_id: groupId,
        rank,
      }),
      reflowBehavior: () => {
        active({
          schemaOption: {
            pageType: articleClass === EArticleClass.VIDEO ? 'video' : 'article',
            groupId,
            authorId: author.userId,
          },
          ampExtra: {
            enter_position: prefix + ClickPosition.related_post,
          },
        });
      },
      defaultBehavior: (e) => {
        e?.stopPropagation();
      },
    },
    [active, groupId, articleClass, author],
  );

  const articleCardRef = useViewTracking<HTMLAnchorElement>({
    onView: useCallback(() => {
      if (isMobileUser) {
        window.Tea('wap_cta_show', {
          current_page_module: currentPageModule,
          current_position: prefix + ClickPosition.related_post,
          rank,
        });
      }

      window.Tea('wap_related_post_show', {
        current_page_module: currentPageModule,
        current_position: prefix + ClickPosition.related_post,
        group_id: groupId,
        media_id: author.userId,
        article_class: articleClass,
        impr_id: imprId,
        rank,
      });
      if (rank >= RELATED_ARTICLE_CAROUSEL_HIDE_RANK) {
        setRelatedPostCarouselShow(false);
      }
    }, [isMobileUser]),
  });

  const showGPTDesc = useAtomValue($RelatedPostAIGCDesc);

  const isVideo = articleClass === 'Video';

  return (
    <Link
      ref={articleCardRef}
      className={cls('article_card', {
        multi_images: posterImageList.length > 1 || (isBot && hitGpt),
      })}
      onClick={handleClickArticle}
      to={articleHref}
    >
      <div className={'article_card_main'}>
        <div className={'article_card_main_images'}>
          {posterImageList.map((image, index) => (
            <div
              style={{
                flex: '1 1 auto',
                width: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div className={'article_card_main_images_image'} style={{ width: 'auto', flex: 'none' }} key={index}>
                <Image
                  src={image.url}
                  fetchpriority={isLCP ? 'high' : 'auto'}
                  alt={showGPTDesc ? image.gptImageDescription : ''}
                />
              </div>
              {isBot && showGPTDesc && image.gptImageDescription ? (
                <div className="image-ocr-text ">{image.gptImageDescription}</div>
              ) : (
                <></>
              )}
            </div>
          ))}
          {isVideo && (
            <div className="video-badge">
              <PlayIcon />
            </div>
          )}
        </div>
        <div className="article_card_main_body">
          {isBot ? (
            <h3 className="article_card_main_body_title">{finalTitle}</h3>
          ) : (
            <div className="article_card_main_body_title">{finalTitle}</div>
          )}
          {isBot && hitGpt && (
            <div className="article_card_main_body_supplements">{unescapeArticleContentChars(supplements)}</div>
          )}
          <div className="article_card_main_body_content">{unescapeArticleContentChars(shortContent)}</div>
        </div>
      </div>
      <DivisibleStack wrapper={'article_card_desc'} divider={'article_card_desc_divider'}>
        <object>
          <Link className={'article_card_user'} to={authorHref} onClick={handleClickAuthor}>
            <Image src={author.avatar} alt={author.nickName} />
            <p>{author.nickName}</p>
          </Link>
        </object>
        <div className={'article_card_like'}>
          <Heart />
          <p>{t('like_count_plural', { num: articleLikes }).replace('%d', formatNum(articleLikes))}</p>
        </div>
      </DivisibleStack>
    </Link>
  );
};
